<template>
    <div class="form-input" :class="classes" :style="[{ width }, $attrs.style]">
        <div v-if="$slots.prepend" class="form-input__prepend">
            <slot name="prepend" />
        </div>
        <input
            v-if="autowidth"
            :id="context.name || $attrs.name"
            ref="input"
            v-model="innerValue"
            v-maska
            v-autowidth="autowidth"
            :data-maska="mask"
            :type="type"
            class="form-input__input"
            v-bind="inputAttrs"
            :style="inputStyle"
            :required="context.required || $attrs.required"
            :name="context.name || $attrs.name"
            @input="$emit('update:modelValue', $event.target.value)"
            @change="$emit('change', $event.target.value)"
            @focus="onFocus"
            @blur="onBlur"
            @keyup.enter="onEnterKey"
        />
        <input
            v-else
            :id="context.name || $attrs.name"
            ref="input"
            v-model="innerValue"
            v-maska
            :data-maska="mask"
            :style="inputStyle"
            :type="type"
            v-bind="inputAttrs"
            :required="context.required || $attrs.required"
            :name="context.name || $attrs.name"
            class="form-input__input"
            @input="$emit('update:modelValue', $event.target.value)"
            @change="$emit('change', $event.target.value)"
            @focus="onFocus"
            @blur="onBlur"
            @keyup.enter="onEnterKey"
        />
        <div v-if="$slots.append || clearable || editable" class="form-input__append">
            <slot name="append" />
            <div class="form-input__append-controls">
                <button
                    v-if="editable && focused"
                    class="button button--blank"
                    type="button"
                    @click.stop="blur"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="10"
                        height="10"
                    >
                        <path
                            d="M504.502 75.496c-9.997-9.998-26.205-9.998-36.204 0L161.594 382.203 43.702 264.311c-9.997-9.998-26.205-9.997-36.204 0-9.998 9.997-9.998 26.205 0 36.203l135.994 135.992c9.994 9.997 26.214 9.99 36.204 0L504.502 111.7c9.998-9.997 9.997-26.206 0-36.204z"
                        />
                    </svg>
                </button>
                <button
                    v-if="editable && !focused"
                    class="button button--blank"
                    type="button"
                    @click.stop="focus"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="10"
                        height="10"
                    >
                        <path
                            fill="#666882"
                            d="M328.883 89.125l107.59 107.589-272.34 272.34L56.604 361.465l272.279-272.34zm189.23-25.948l-47.981-47.981c-18.543-18.543-48.653-18.543-67.259 0l-45.961 45.961 107.59 107.59 53.611-53.611c14.382-14.383 14.382-37.577 0-51.959zM.3 512.69c-1.958 8.812 5.998 16.708 14.811 14.565l119.891-29.069L27.473 390.597.3 512.69z"
                        />
                    </svg>
                </button>
            </div>
            <div class="form-input__append-controls" @click.stop>
                <button
                    v-if="clearable"
                    class="button button--blank"
                    type="button"
                    @click.stop="clear"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="10"
                        height="10"
                    >
                        <path
                            fill="#666882"
                            d="M505.943 6.058c-8.077-8.077-21.172-8.077-29.249 0L6.058 476.693c-8.077 8.077-8.077 21.172 0 29.249A20.612 20.612 0 0020.683 512a20.614 20.614 0 0014.625-6.059L505.943 35.306c8.076-8.076 8.076-21.171 0-29.248z"
                        />
                        <path
                            fill="#666882"
                            d="M505.942 476.694L35.306 6.059c-8.076-8.077-21.172-8.077-29.248 0-8.077 8.076-8.077 21.171 0 29.248l470.636 470.636a20.616 20.616 0 0014.625 6.058 20.615 20.615 0 0014.624-6.057c8.075-8.078 8.075-21.173-.001-29.25z"
                        />
                    </svg>
                </button>
            </div>
        </div>
        <!-- @TODO remove error view here due to FormField use -->
        <transition name="fade">
            <span v-if="error" class="form-input__error">
                {{ error[0] }}
            </span>
        </transition>
    </div>
</template>

<script>
import { vMaska } from 'maska/vue';

const noop = () => {};
const empty = () => ({});

export default {
    name: 'FormInput',
    directives: {
        maska: vMaska,
    },
    inject: {
        context: {
            default: empty,
        },
        registerControl: {
            default: () => noop,
        },
    },
    inheritAttrs: false,
    props: {
        modelValue: {},
        error: {
            type: [Object, Array],
            default: null,
        },
        label: String,
        autowidth: {
            type: [Boolean, Object],
            default: null,
        },
        mask: {
            type: String,
            default: null,
        },
        width: {
            type: [String, Number],
            default: '100%',
        },
        inputStyle: {
            type: Object,
            default: () => {},
        },
        type: {
            type: String,
            default: 'text',
        },
        success: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue', 'change', 'focus', 'blur', 'enter'],
    data() {
        return {
            // this is just to make maska work normally
            innerValue: this.modelValue,

            focused: false,
        };
    },
    computed: {
        classes() {
            let classes = [];

            if (this.theme && typeof this.theme === 'string') {
                return `form-input--theme-${this.theme}`;
            }

            if (this.$slots.prepend) {
                classes.push('form-input--prepend');
            }

            if (this.$slots.append) {
                classes.push('form-input--append');
            }

            if (this.error || this.context.error) {
                classes.push('has-error');
            }

            if (this.success) {
                classes.push('success');
            }

            if (this.clearable) {
                classes.push('form-input--editable');
            }

            return [...classes, this.$attrs.class];
        },

        inputAttrs() {
            // class/style stay on the wrapper (Vue 2 merged them there);
            // everything else falls through to the input
            const { class: _class, style: _style, ...attrs } = this.$attrs;
            return attrs;
        },
    },

    watch: {
        modelValue() {
            this.innerValue = this.modelValue;
        },
    },

    mounted() {
        this.registerControl(this);
    },

    methods: {
        onFocus() {
            this.$emit('focus');
            this.focused = true;
        },
        onBlur() {
            this.$emit('blur');
            this.focused = false;
        },
        focus() {
            this.$refs.input.focus();
        },
        blur() {
            this.$refs.input.blur();
        },
        onEnterKey() {
            this.$emit('enter');
            this.blur();
        },
        clear() {
            this.$emit('update:modelValue', '');
            this.$emit('change', '');
            this.blur();
        },
    },
};
</script>

<style lang="scss" src="./FormInput.scss"></style>
